import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import { toast } from 'sonner';
import { 
  Brain, Building2, Briefcase, User, Plus, Trash2, Save, 
  FolderOpen, Settings, Sparkles, X, ChevronRight, Edit2
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { 
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger 
} from '@/components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '@/components/ui/select';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export function MemoryManager({ isOpen, onClose }) {
  const [memory, setMemory] = useState(null);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [newFact, setNewFact] = useState('');
  const [activeTab, setActiveTab] = useState('profile'); // profile, projects, facts
  
  // Form state
  const [company, setCompany] = useState('');
  const [industry, setIndustry] = useState('');
  const [role, setRole] = useState('');
  const [tone, setTone] = useState('professional');
  const [detailLevel, setDetailLevel] = useState('medium');
  
  // Project form
  const [showProjectForm, setShowProjectForm] = useState(false);
  const [projectName, setProjectName] = useState('');
  const [projectDesc, setProjectDesc] = useState('');
  const [projectContext, setProjectContext] = useState('');

  const token = localStorage.getItem('token');

  useEffect(() => {
    if (isOpen) {
      fetchMemory();
      fetchProjects();
    }
  }, [isOpen]);

  const fetchMemory = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/house-agents/memory/profile`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMemory(res.data);
      setCompany(res.data.company || '');
      setIndustry(res.data.industry || '');
      setRole(res.data.role || '');
      setTone(res.data.preferences?.tone || 'professional');
      setDetailLevel(res.data.preferences?.detail_level || 'medium');
    } catch (err) {
      console.error('Failed to fetch memory:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchProjects = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/house-agents/projects`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setProjects(res.data.projects || []);
    } catch (err) {
      console.error('Failed to fetch projects:', err);
    }
  };

  const saveProfile = async () => {
    setSaving(true);
    try {
      await axios.put(`${API_URL}/api/house-agents/memory/profile`, {
        company,
        industry,
        role,
        preferences: { tone, detail_level: detailLevel }
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Profile saved! Agents will remember this.');
      fetchMemory();
    } catch (err) {
      toast.error('Failed to save profile');
    } finally {
      setSaving(false);
    }
  };

  const addFact = async () => {
    if (!newFact.trim()) return;
    try {
      await axios.post(`${API_URL}/api/house-agents/memory/facts`, 
        { fact: newFact },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Fact added!');
      setNewFact('');
      fetchMemory();
    } catch (err) {
      toast.error('Failed to add fact');
    }
  };

  const deleteFact = async (factId) => {
    try {
      await axios.delete(`${API_URL}/api/house-agents/memory/facts/${factId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Fact removed');
      fetchMemory();
    } catch (err) {
      toast.error('Failed to remove fact');
    }
  };

  const createProject = async () => {
    if (!projectName.trim()) return;
    try {
      await axios.post(`${API_URL}/api/house-agents/projects`, {
        name: projectName,
        description: projectDesc,
        context: projectContext
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Project created!');
      setShowProjectForm(false);
      setProjectName('');
      setProjectDesc('');
      setProjectContext('');
      fetchProjects();
    } catch (err) {
      toast.error('Failed to create project');
    }
  };

  const deleteProject = async (projectId) => {
    try {
      await axios.delete(`${API_URL}/api/house-agents/projects/${projectId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Project deleted');
      fetchProjects();
    } catch (err) {
      toast.error('Failed to delete project');
    }
  };

  if (!isOpen) return null;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl bg-zinc-900 border-zinc-800 text-white max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-3 text-xl">
            <div className="p-2 rounded-lg bg-gradient-to-br from-teal-500/20 to-green-500/20">
              <Brain className="w-5 h-5 text-teal-400" />
            </div>
            Memory & Projects
          </DialogTitle>
        </DialogHeader>

        {/* Tabs */}
        <div className="flex gap-2 border-b border-zinc-800 pb-3 mb-4">
          {[
            { id: 'profile', label: 'Profile', icon: User },
            { id: 'facts', label: 'Facts', icon: Sparkles },
            { id: 'projects', label: 'Projects', icon: FolderOpen }
          ].map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-all ${
                activeTab === tab.id 
                  ? 'bg-teal-500/20 text-teal-400' 
                  : 'text-zinc-400 hover:text-white hover:bg-zinc-800'
              }`}
            >
              <tab.icon className="w-4 h-4" />
              {tab.label}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin w-8 h-8 border-2 border-teal-500 border-t-transparent rounded-full" />
          </div>
        ) : (
          <AnimatePresence mode="wait">
            {/* Profile Tab */}
            {activeTab === 'profile' && (
              <motion.div
                key="profile"
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                className="space-y-4"
              >
                <p className="text-sm text-zinc-400 mb-4">
                  Agents will remember this information across all conversations.
                </p>
                
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm text-zinc-400 mb-1 block">Company Name</label>
                    <div className="relative">
                      <Building2 className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
                      <Input
                        value={company}
                        onChange={(e) => setCompany(e.target.value)}
                        placeholder="Acme Inc"
                        className="pl-10 bg-zinc-800 border-zinc-700"
                      />
                    </div>
                  </div>
                  
                  <div>
                    <label className="text-sm text-zinc-400 mb-1 block">Industry</label>
                    <div className="relative">
                      <Briefcase className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
                      <Input
                        value={industry}
                        onChange={(e) => setIndustry(e.target.value)}
                        placeholder="Technology"
                        className="pl-10 bg-zinc-800 border-zinc-700"
                      />
                    </div>
                  </div>
                </div>

                <div>
                  <label className="text-sm text-zinc-400 mb-1 block">Your Role</label>
                  <Input
                    value={role}
                    onChange={(e) => setRole(e.target.value)}
                    placeholder="Marketing Manager"
                    className="bg-zinc-800 border-zinc-700"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm text-zinc-400 mb-1 block">Preferred Tone</label>
                    <Select value={tone} onValueChange={setTone}>
                      <SelectTrigger className="bg-zinc-800 border-zinc-700">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="professional">Professional</SelectItem>
                        <SelectItem value="casual">Casual</SelectItem>
                        <SelectItem value="formal">Formal</SelectItem>
                        <SelectItem value="friendly">Friendly</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  
                  <div>
                    <label className="text-sm text-zinc-400 mb-1 block">Detail Level</label>
                    <Select value={detailLevel} onValueChange={setDetailLevel}>
                      <SelectTrigger className="bg-zinc-800 border-zinc-700">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="brief">Brief</SelectItem>
                        <SelectItem value="medium">Medium</SelectItem>
                        <SelectItem value="detailed">Detailed</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <Button 
                  onClick={saveProfile} 
                  disabled={saving}
                  className="w-full bg-gradient-to-r from-teal-500 to-green-500 hover:from-teal-600 hover:to-green-600"
                >
                  {saving ? 'Saving...' : 'Save Profile'}
                  <Save className="w-4 h-4 ml-2" />
                </Button>
              </motion.div>
            )}

            {/* Facts Tab */}
            {activeTab === 'facts' && (
              <motion.div
                key="facts"
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                className="space-y-4"
              >
                <p className="text-sm text-zinc-400 mb-4">
                  Add specific facts you want agents to always remember.
                </p>

                <div className="flex gap-2">
                  <Input
                    value={newFact}
                    onChange={(e) => setNewFact(e.target.value)}
                    placeholder="Our target audience is small businesses..."
                    className="bg-zinc-800 border-zinc-700"
                    onKeyDown={(e) => e.key === 'Enter' && addFact()}
                  />
                  <Button onClick={addFact} size="icon" className="bg-teal-500 hover:bg-teal-600">
                    <Plus className="w-4 h-4" />
                  </Button>
                </div>

                <div className="space-y-2 max-h-64 overflow-y-auto">
                  {memory?.facts?.length > 0 ? (
                    memory.facts.map((fact) => (
                      <div 
                        key={fact.id}
                        className="flex items-start gap-3 p-3 rounded-lg bg-zinc-800/50 border border-zinc-700/50 group"
                      >
                        <Sparkles className="w-4 h-4 text-teal-400 mt-0.5 flex-shrink-0" />
                        <p className="text-sm text-zinc-300 flex-1">{fact.content}</p>
                        <button
                          onClick={() => deleteFact(fact.id)}
                          className="opacity-0 group-hover:opacity-100 text-red-400 hover:text-red-300 transition-opacity"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    ))
                  ) : (
                    <div className="text-center py-8 text-zinc-500">
                      No facts saved yet. Add something agents should remember!
                    </div>
                  )}
                </div>
              </motion.div>
            )}

            {/* Projects Tab */}
            {activeTab === 'projects' && (
              <motion.div
                key="projects"
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                className="space-y-4"
              >
                <div className="flex items-center justify-between mb-4">
                  <p className="text-sm text-zinc-400">
                    Organize your work into projects for better context.
                  </p>
                  <Button 
                    onClick={() => setShowProjectForm(!showProjectForm)}
                    size="sm"
                    className="bg-teal-500 hover:bg-teal-600"
                  >
                    <Plus className="w-4 h-4 mr-1" />
                    New Project
                  </Button>
                </div>

                {showProjectForm && (
                  <motion.div 
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: 'auto' }}
                    className="p-4 rounded-lg bg-zinc-800 border border-zinc-700 space-y-3"
                  >
                    <Input
                      value={projectName}
                      onChange={(e) => setProjectName(e.target.value)}
                      placeholder="Project name"
                      className="bg-zinc-900 border-zinc-700"
                    />
                    <Input
                      value={projectDesc}
                      onChange={(e) => setProjectDesc(e.target.value)}
                      placeholder="Brief description"
                      className="bg-zinc-900 border-zinc-700"
                    />
                    <Textarea
                      value={projectContext}
                      onChange={(e) => setProjectContext(e.target.value)}
                      placeholder="Context for agents (target audience, goals, constraints...)"
                      className="bg-zinc-900 border-zinc-700 min-h-[80px]"
                    />
                    <div className="flex gap-2">
                      <Button onClick={createProject} className="bg-green-500 hover:bg-green-600">
                        Create Project
                      </Button>
                      <Button variant="ghost" onClick={() => setShowProjectForm(false)}>
                        Cancel
                      </Button>
                    </div>
                  </motion.div>
                )}

                <div className="space-y-2">
                  {projects.length > 0 ? (
                    projects.map((project) => (
                      <div 
                        key={project.id}
                        className="p-4 rounded-lg bg-zinc-800/50 border border-zinc-700/50 hover:border-teal-500/30 transition-colors group"
                      >
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <h4 className="font-medium text-white flex items-center gap-2">
                              <FolderOpen className="w-4 h-4 text-teal-400" />
                              {project.name}
                            </h4>
                            {project.description && (
                              <p className="text-sm text-zinc-400 mt-1">{project.description}</p>
                            )}
                            {project.context && (
                              <p className="text-xs text-zinc-500 mt-2 line-clamp-2">{project.context}</p>
                            )}
                          </div>
                          <button
                            onClick={() => deleteProject(project.id)}
                            className="opacity-0 group-hover:opacity-100 text-red-400 hover:text-red-300 transition-opacity p-1"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                    ))
                  ) : (
                    <div className="text-center py-8 text-zinc-500">
                      No projects yet. Create one to organize your work!
                    </div>
                  )}
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        )}
      </DialogContent>
    </Dialog>
  );
}

export default MemoryManager;
